import { useCallback } from 'react';
import { ISurveyResponse } from '../shared/interfaces/ISurveyResponse';
import * as Excel from 'exceljs';
import SurveyUtils from '../shared/SurveyUtils';

const useExportToExcel = () => {
  const addCell = (
    worksheet: any,
    column: string,
    row: number,
    content: string,
    center: boolean,
    bold: boolean
  ) => {
    worksheet.getCell(column + row).value = content;
    worksheet.getCell(column + row).font = { size: 10, bold: bold };
    if (center) {
      worksheet.getCell(column + row).alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
    }
    worksheet.getCell(column + row).border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  };

  const addCol = (current: string) => {
    const alphas = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    let found = false;
    for (let i = 0; i < alphas.length; i++) {
      if (found) {
        return alphas[i];
      }
      if (alphas[i] == current) {
        found = true;
      }
    }
    return 'A';
  };

  const addColX = (current: string, count: number) => {
    for (count > 0; count > 0; count--) {
      current = addCol(current);
    }
    return current;
  };

  const exportToExcel = useCallback(
    async (
      surveyResponses: ISurveyResponse[],
      label: string,
      platform: string,
      companyName: string,
      slug: string,
      type: string,
      customAnswers: {
        answerOne: string;
        answerTwo: string;
        answerThree: string;
        answerFour: string;
        answerFive: string;
      },
      imageBase64: string,
      archived: boolean,
      archivedAt: string | null
    ) => {
      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet('Übersicht', {
        properties: { tabColor: { argb: 'FFFFFFFF' } },
        views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
      });
      const today = new Date();
      const formattedDate = today.toISOString().slice(0, 10);
      const title = `Reporting Reputationsmanagement | ${companyName} | ${
        archived ? archivedAt?.slice(0, 10) : formattedDate
      }`;
      const row = worksheet.getRow(13);
      let positiveCount = 0;
      let negativeCount = 0;
      let neutralCount = 0;
      let total = 0;
      let npsPositiveCount = 0;
      let npsNegativeCount = 0;
      let npsNeutralCount = 0;
      let npsTotalCount = 0;
      let standardPositiveCount = 0;
      let standardNegativeCount = 0;
      let standardTotal = 0;
      let customTotal = 0;
      let customPositiveCount = 0;
      let customNegativeCount = 0;
      let customResponses: any = {};
      let customSorted: ISurveyResponse[] = [];

      // Custom-Berechnung start
      if (type === 'CUSTOMSURVEY') {
        const customEval = SurveyUtils.evaluateCustom(surveyResponses);
        customSorted = customEval.sorted;
        customTotal = customEval.total;
        customPositiveCount = customEval.completed;
        customNegativeCount = customEval.abborted;
        customResponses = customEval.responses;
        // Custom-Berechnung end
      }

      if (type === 'NPS') {
        // NPS-Berechnung start
        const npsAllAnswers = surveyResponses.flatMap((response) =>
          response.answers ? response.answers : []
        );
        npsPositiveCount = npsAllAnswers.filter((item) => item.rating >= 9).length;
        npsNegativeCount = npsAllAnswers.filter((item) => item.rating <= 6).length;
        npsNeutralCount = npsAllAnswers.filter(
          (item) => item.rating >= 7 && item.rating <= 8
        ).length;
        npsTotalCount = surveyResponses.reduce((acc, response) => acc + response.answers.length, 0);
      }
      // NPS-Berechnung end

      // Standard-Berechnung start
      if (type === 'STANDARD') {
        const score = surveyResponses[0].score;

        standardTotal = surveyResponses[0].totalCount;
        standardPositiveCount = 0;
        standardNegativeCount = 0;
        let minLength = Math.min(
          surveyResponses[0].answers.length,
          surveyResponses[1].answers.length
        );
        let sumOfPairs = [];

        for (let i = 0; i < minLength; i++) {
          sumOfPairs.push(
            surveyResponses[0].answers[i].rating + surveyResponses[1].answers[i].rating
          );
        }

        let lastAnswerArray = surveyResponses[2].answers.map((answer) => answer.rating);

        sumOfPairs.forEach((pair, index) => {
          if (index < lastAnswerArray.length) {
            if (pair >= score && lastAnswerArray[index] === 1) {
              standardPositiveCount += 1;
            } else {
              standardNegativeCount += 1;
            }
          }
        });
      }
      // Standard-Berechnung end

      // Barometer-Berechnung start
      if (type === 'BAROMETER') {
        surveyResponses.forEach((response, index) => {
          if (response.mood === 'NEGATIVE') negativeCount += response.answers.length;
          if (response.mood === 'NEUTRAL') neutralCount += response.answers.length;
          if (response.mood === 'POSITIVE') positiveCount += response.answers.length;
          total += response.answers.length;
        });
        // Barometer-Berechnung end
      }

      // second worksheet start
      if (type === 'CUSTOMSURVEY') {
        const title = `Reporting Reputationsmanagement | ${companyName} | ${
          archived ? archivedAt?.slice(0, 10) : formattedDate
        }`;
        const worksheetSecond = workbook.addWorksheet('Details', {
          properties: { tabColor: { argb: 'FFFFFFFF' } },
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });

        worksheetSecond.mergeCells('C1:Z1');
        worksheetSecond.getCell('C1').value = title;
        worksheetSecond.getCell('C1').alignment = { horizontal: 'left', vertical: 'middle' };
        worksheetSecond.getCell('C1').font = {
          name: 'Sagoe UI',
          size: 14,
          bold: true,
          color: { argb: 'FF00C7CF' },
        };

        let currentColumn = 'B';

        // add the headers of the table
        addCell(worksheetSecond, currentColumn, 11, 'Datum', true, true);
        worksheetSecond.getColumn(currentColumn).width = 20;
        currentColumn = addCol(currentColumn);

        customSorted.forEach((question) => {
          addCell(
            worksheetSecond,
            currentColumn,
            11,
            `Frage ${question.position} \n (${question.question})`,
            false,
            true
          );
          worksheetSecond.getColumn(currentColumn).width = 60;

          currentColumn = addCol(currentColumn);
        });

        addCell(worksheetSecond, currentColumn, 11, 'Auswertung', true, true);
        worksheetSecond.getColumn(currentColumn).width = 20;

        // add the responses
        let customResponseRow = 12;

        const customResponseIds = Object.keys(customResponses);
        customResponseIds.sort((a, b) => {
          const ad = customResponses[a][0].createdAt;
          const bd = customResponses[b][0].createdAt;
          return ad > bd ? 1 : bd > ad ? -1 : 0;
        });

        customResponseIds.forEach((rid: any) => {
          const answers = customResponses[rid];

          currentColumn = 'B';
          addCell(
            worksheetSecond,
            currentColumn,
            customResponseRow,
            answers[0].createdAt.slice(0, 10) + ' ' + answers[0].createdAt.slice(11, 16),
            true,
            false
          );
          currentColumn = addCol(currentColumn);

          surveyResponses.forEach((question, index) => {
            const answer = answers.find((answer: any) => answer.questionId == question.id);

            addCell(
              worksheetSecond,
              currentColumn,
              customResponseRow,
              answer?.feedback || '',
              false,
              false
            );
            currentColumn = addCol(currentColumn);
          });

          addCell(
            worksheetSecond,
            currentColumn,
            customResponseRow,
            SurveyUtils.isCompleted(answers, customSorted) ? 'Beendet' : 'Abgebrochen',
            true,
            false
          );

          customResponseRow++;
        });
      } else if (type === 'STANDARD') {
        const title = `Reporting Reputationsmanagement | ${companyName} | ${
          archived ? archivedAt?.slice(0, 10) : formattedDate
        }`;
        const worksheetSecond = workbook.addWorksheet('Details', {
          properties: { tabColor: { argb: 'FFFFFFFF' } },
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });

        const answerArray = surveyResponses.map((response) =>
          response.answers.map((answer) => answer.rating)
        );

        worksheetSecond.mergeCells('C1:H1');
        worksheetSecond.getCell('C1').value = title;
        worksheetSecond.getCell('C1').alignment = { horizontal: 'left', vertical: 'middle' };
        worksheetSecond.getCell('C1').font = {
          name: 'Sagoe UI',
          size: 14,
          bold: true,
          color: { argb: 'FF00C7CF' },
        };

        worksheetSecond.mergeCells('C10:E10');
        worksheetSecond.getCell('C10').value = 'Rating';
        worksheetSecond.getCell('C10').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheetSecond.getCell('C10').font = { size: 10, bold: true, name: 'Sagoe UI' };
        worksheetSecond.getCell('C10').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('F11').value = 'Auswertung';
        worksheetSecond.getCell('F11').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheetSecond.getCell('F11').font = { size: 10, bold: true, name: 'Sagoe UI' };
        worksheetSecond.getCell('F11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('F').width = 20;

        worksheetSecond.getCell('B11').value = 'Datum';
        worksheetSecond.getCell('B11').font = { size: 10, bold: true };
        worksheetSecond.getCell('B11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('B11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('B').width = 20;

        worksheetSecond.getCell('C11').value = `Frage 1 \n (${surveyResponses[0].question})`;
        worksheetSecond.getCell('C11').font = { size: 10, bold: true };
        worksheetSecond.getCell('C11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('C').width = 60;

        worksheetSecond.getCell('D11').value = `Frage 2 \n (${surveyResponses[1].question})`;
        worksheetSecond.getCell('D11').font = { size: 10, bold: true };
        worksheetSecond.getCell('D11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('D').width = 60;

        worksheetSecond.getCell('E11').value = `Frage 3 \n (${surveyResponses[2].question})`;
        worksheetSecond.getCell('E11').font = { size: 10, bold: true };
        worksheetSecond.getCell('E11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('E').width = 50;

        worksheetSecond.getCell('G11').value = 'Feedback';
        worksheetSecond.getCell('G11').font = { size: 10, bold: true };
        worksheetSecond.getCell('G11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getColumn('G').width = 100;
        worksheetSecond.getCell('G11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        //loop start

        interface RowData {
          B: string;
          C: string | number;
          D: string | number;
          E: string;
          F: string;
          G: string | undefined;
        }

        const rowsData: RowData[] = [];

        const answerPattern = (pos: number, index: number) => {
          const answer = answerArray[pos][index];

          let cellValue;

          switch (answer) {
            case 1:
              cellValue = customAnswers.answerFive;
              break;
            case 2:
              cellValue = customAnswers.answerFour;
              break;
            case 3:
              cellValue = customAnswers.answerThree;
              break;
            case 4:
              cellValue = customAnswers.answerTwo;
              break;
            case 5:
              cellValue = customAnswers.answerOne;
              break;
            default:
              cellValue = 'no value';
          }
          return cellValue;
        };

        surveyResponses[0].answers.forEach((_, y) => {
          const summary = answerArray[0][y] + answerArray[1][y];
          const answerC = answerPattern(0, y);
          const answerD = answerPattern(1, y);
          const answerE = answerArray[2][y] === 1 ? 'ja' : 'nein';

          rowsData.push({
            B: surveyResponses[0].answers[y].createdAt.slice(0, 10),
            C: answerC,
            D: answerD,
            E: answerE,
            F:
              surveyResponses[2].answers[y].rating === 1 && summary >= surveyResponses[0].score
                ? 'Positiv'
                : 'Negativ',
            G: surveyResponses[2].answers[y].feedback
              ? surveyResponses[2].answers[y].feedback
              : '-',
          });
        });

        rowsData.sort((a, b) => new Date(b.B).getTime() - new Date(a.B).getTime());

        rowsData.forEach((data, index) => {
          const rowIndex = index + 12;

          worksheetSecond.getCell(`B${rowIndex}`).value = data.B;
          worksheetSecond.getCell(`C${rowIndex}`).value = data.C;
          worksheetSecond.getCell(`D${rowIndex}`).value = data.D;
          worksheetSecond.getCell(`E${rowIndex}`).value = data.E;
          worksheetSecond.getCell(`F${rowIndex}`).value = data.F;
          worksheetSecond.getCell(`G${rowIndex}`).value = data.G;

          ['B', 'C', 'D', 'E', 'F', 'G'].forEach((col) => {
            const cell = worksheetSecond.getCell(`${col}${rowIndex}`);
            cell.font = { size: 10 };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'center',
              wrapText: false,
              shrinkToFit: false,
            };
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            };
            worksheetSecond.getColumn('G').width = 100;
            worksheetSecond.getRow(rowIndex).height = 14;
            if (data.G && data.G.length > 100) {
              worksheetSecond.getCell(`G${rowIndex}`).alignment = {
                vertical: 'middle',
                horizontal: 'right',
              };
            }
          });
        });
      } else {
        const title = `Reporting Reputationsmanagement | ${companyName} | ${
          archived ? archivedAt?.slice(0, 10) : formattedDate
        }`;
        const worksheetSecond = workbook.addWorksheet('Details', {
          properties: { tabColor: { argb: 'FFFFFFFF' } },
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });
        const rowSecond = worksheetSecond.getRow(12);

        worksheetSecond.mergeCells('C1:H1');
        worksheetSecond.getCell('C1').value = title;
        worksheetSecond.getCell('C1').alignment = { horizontal: 'left', vertical: 'middle' };
        worksheetSecond.getCell('C1').font = {
          name: 'Sagoe UI',
          size: 14,
          bold: true,
          color: { argb: 'FF00C7CF' },
        };

        worksheetSecond.getCell('C10').value = 'Auswertung';
        worksheetSecond.getCell('C10').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheetSecond.getCell('C10').font = { size: 10, bold: true, name: 'Sagoe UI' };
        worksheetSecond.getCell('C10').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('B11').value = 'Datum';
        worksheetSecond.getCell('B11').font = { size: 10, bold: true };
        worksheetSecond.getCell('B11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('B11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('B').width = 20;
        rowSecond.getCell('B').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        rowSecond.getCell('B').value = surveyResponses[0].createdAt.slice(0, 10);

        worksheetSecond.getCell('C11').value = 'Rating';
        worksheetSecond.getCell('C11').font = { size: 10, bold: true };
        worksheetSecond.getCell('C11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('C11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('C').width = 30;
        rowSecond.getCell('C').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('D11').value = 'Feedback';
        worksheetSecond.getCell('D11').font = { size: 10, bold: true };
        worksheetSecond.getCell('D11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('D11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getColumn('D').width = 100;
        rowSecond.getCell('D').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        //loop start

        interface RowData {
          B: string;
          C: string | number;
          D: string;
        }

        const rowsData: RowData[] = [];

        surveyResponses.forEach((response) => {
          response.answers.forEach((answer) => {
            if (!answer.createdAt) return;

            rowsData.push({
              B: answer.createdAt.slice(0, 10) || '-',
              C: response.mood || answer.rating || '-',
              D: answer.feedback ? answer.feedback : '-',
            });
          });
        });

        rowsData.sort((a, b) => new Date(b.B).getTime() - new Date(a.B).getTime());

        rowsData.forEach((data, index) => {
          const rowIndex = index + 12;

          worksheetSecond.getCell(`B${rowIndex}`).value = data.B;
          worksheetSecond.getCell(`C${rowIndex}`).value = data.C;
          worksheetSecond.getCell(`D${rowIndex}`).value = data.D;

          ['B', 'C', 'D'].forEach((col) => {
            const cell = worksheetSecond.getCell(`${col}${rowIndex}`);
            cell.font = { size: 10 };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'center',
              wrapText: false,
              shrinkToFit: false,
            };
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            };
            if (data.D.length > 100) {
              worksheetSecond.getCell(`D${rowIndex}`).alignment = {
                vertical: 'middle',
                horizontal: 'right',
              };
            }
          });
          worksheetSecond.getColumn('D').width = 100;
        });
        //loop end
      }
      // second worksheet end

      // first worksheet start
      worksheet.mergeCells('C1:H1');
      worksheet.getCell('C1').value = title;
      worksheet.getCell('C1').alignment = { horizontal: 'fill', vertical: 'middle' };
      worksheet.getCell('C1').font = {
        name: 'Sagoe UI',
        size: 14,
        bold: true,
        color: { argb: 'FF00C7CF' },
      };

      let currentColumn = 'B';

      if (type != 'CUSTOMSURVEY') {
        worksheet.getCell(currentColumn + '12').value = 'Plattform';
        worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
        worksheet.getCell(currentColumn + '12').alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        worksheet.getCell(currentColumn + '12').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheet.getColumn(currentColumn).width = 20;
        worksheet.getRow(13).height = 30;
        row.getCell(currentColumn).border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        row.getCell(currentColumn).value = platform ? `${platform}` : '';
        currentColumn = addCol(currentColumn);
      }

      worksheet.getCell(currentColumn + '12').value = 'Umfragelabel';
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 35;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value = label;

      currentColumn = addCol(currentColumn);
      const mergeCols = type !== 'STANDARD' && type != 'CUSTOMSURVEY' ? 3 : 2;
      const mergeEndCol = addColX(currentColumn, mergeCols);
      worksheet.mergeCells(currentColumn + '11:' + mergeEndCol + '11');
      worksheet.getCell(currentColumn + '11').value = 'Auswertung';
      worksheet.getCell(currentColumn + '11').alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      worksheet.getCell(currentColumn + '11').font = { size: 10, bold: true, name: 'Sagoe UI' };
      worksheet.getCell(currentColumn + '11').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };

      worksheet.getCell(currentColumn + '12').value = 'Gesamtzahl';
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 15;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value =
        type === 'NPS'
          ? npsTotalCount
          : 0 || type === 'STANDARD'
          ? standardTotal
          : 0 || type === 'CUSTOMSURVEY'
          ? customTotal
          : 0 || type === 'BAROMETER'
          ? total
          : 0;

      currentColumn = addCol(currentColumn);
      let labelPositive = 'Positiv';
      let labelNeutral = 'Neutral';
      let labelNegative = 'Negativ';

      if (type === 'CUSTOMSURVEY') {
        labelPositive = 'Beendet';
        labelNegative = 'Abgebrochen';
      }

      worksheet.getCell(currentColumn + '12').value = labelPositive;
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 10;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value =
        type === 'NPS'
          ? npsPositiveCount
          : 0 || type === 'STANDARD'
          ? standardPositiveCount
          : 0 || type === 'CUSTOMSURVEY'
          ? customPositiveCount
          : 0 || type === 'BAROMETER'
          ? positiveCount
          : 0;
      currentColumn = addCol(currentColumn);

      if (type !== 'STANDARD' && type !== 'CUSTOMSURVEY') {
        worksheet.getCell(currentColumn + '12').value = labelNeutral;
        worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
        worksheet.getCell(currentColumn + '12').alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        worksheet.getCell(currentColumn + '12').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheet.getColumn(currentColumn).width = 10;
        row.getCell(currentColumn).border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        row.getCell(currentColumn).value =
          type === 'NPS' ? npsNeutralCount : 0 || type === 'BAROMETER' ? neutralCount : 0;

        currentColumn = addCol(currentColumn);
      }

      worksheet.getCell(currentColumn + '12').value = labelNegative;
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 10;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value =
        type === 'NPS'
          ? npsNegativeCount
          : 0 || type === 'STANDARD'
          ? standardNegativeCount
          : 0 || type === 'CUSTOMSURVEY'
          ? customNegativeCount
          : 0 || type === 'BAROMETER'
          ? negativeCount
          : 0;

      currentColumn = addCol(currentColumn);

      worksheet.getCell(currentColumn + '12').value = 'URL zur Umfrage';
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 50;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      const urlPath = SurveyUtils.getPathByType(type);
      row.getCell(
        currentColumn
      ).value = `${process.env.REACT_APP_FRONTEND_BASE_URL}/${urlPath}/${slug}`;

      // first worksheet end

      function saveAs(blob: any, fileName: string) {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      const fileName = `${label}_${platform}_${companyName}_${slug}.xlsx`;
      workbook.xlsx
        .writeBuffer()
        .then((buffer: any) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, fileName);
        })
        .catch((err: any) => console.error('Error writing excel export', err));
    },
    []
  );

  return exportToExcel;
};

export default useExportToExcel;
